import type { RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { VoiceCallUi } from "../lib/webrtcCall";

/** Удержание ~0.8s — чтобы щекой/ухом не сбрасывать разговор в PWA (нет датчика приближения). */
const HANGUP_HOLD_MS = 820;

/** Генерирует двухтональный сигнал вызова (440+480 Hz) с паузами 1s/2s. */
function useRingtone(active: boolean) {
  useEffect(() => {
    if (!active) return;
    let stopped = false;
    let ctx: AudioContext | null = null;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const playRing = () => {
      if (!ctx || stopped) return;
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.18, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.9);

      for (const freq of [440, 480]) {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = freq;
        osc.connect(gain);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.9);
      }
    };

    try {
      ctx = new AudioContext();
    } catch {
      return;
    }

    // Первый звонок сразу
    playRing();
    // Повтор каждые 3s (1s тон + 2s пауза)
    intervalId = setInterval(playRing, 3000);

    return () => {
      stopped = true;
      if (intervalId !== null) clearInterval(intervalId);
      ctx?.close().catch(() => {});
    };
  }, [active]);
}

const canSetSinkId =
  typeof HTMLAudioElement !== "undefined" && "setSinkId" in HTMLAudioElement.prototype;

type VoiceCallOverlayProps = {
  ui: VoiceCallUi;
  onAccept: () => void;
  onReject: () => void;
  onHangup: () => void;
  onDismissInfo: () => void;
  onToggleMute: () => void;
  remoteAudioRef: RefObject<HTMLAudioElement>;
};

type LongPressHangupProps = {
  onHangup: () => void;
  hint: string;
  label: string;
  ariaLabel: string;
};

function LongPressHangup({ onHangup, hint, label, ariaLabel }: LongPressHangupProps) {
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef(0);
  const doneRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    doneRef.current = false;
    setProgress(0);
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const endHold = useCallback(() => {
    clearTimers();
  }, [clearTimers]);

  const startHold = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      doneRef.current = false;
      startRef.current = performance.now();

      const tick = () => {
        const elapsed = performance.now() - startRef.current;
        const p = Math.min(1, elapsed / HANGUP_HOLD_MS);
        setProgress(p);
        if (p < 1 && !doneRef.current) {
          rafRef.current = requestAnimationFrame(tick);
        }
      };
      rafRef.current = requestAnimationFrame(tick);

      timerRef.current = setTimeout(() => {
        doneRef.current = true;
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
        setProgress(1);
        onHangup();
      }, HANGUP_HOLD_MS);
    },
    [onHangup],
  );

  return (
    <div className="voice-call-overlay__hangup-wrap">
      <p className="voice-call-overlay__hint">{hint}</p>
      <button
        type="button"
        className="voice-call-overlay__btn voice-call-overlay__btn--hold"
        aria-label={ariaLabel}
        onPointerDown={startHold}
        onPointerUp={endHold}
        onPointerCancel={endHold}
        onPointerLeave={(ev) => {
          if (ev.buttons === 0) endHold();
        }}
      >
        <span className="voice-call-overlay__hold-fill" style={{ transform: `scaleX(${progress})` }} aria-hidden />
        <span className="voice-call-overlay__hold-label">{label}</span>
      </button>
    </div>
  );
}

export function VoiceCallOverlay({
  ui,
  onAccept,
  onReject,
  onHangup,
  onDismissInfo,
  onToggleMute,
  remoteAudioRef,
}: VoiceCallOverlayProps) {
  const [speakerOn, setSpeakerOn] = useState(false);

  const toggleSpeaker = useCallback(() => {
    const audioEl = remoteAudioRef.current;
    if (!audioEl || !canSetSinkId) return;
    const next = !speakerOn;
    setSpeakerOn(next);
    const el = audioEl as HTMLAudioElement & { setSinkId: (id: string) => Promise<void> };
    void (async () => {
      try {
        if (next) {
          // Громкая связь: системный вывод по умолчанию (динамик / speakers)
          await el.setSinkId("");
        } else {
          try {
            // Тихо: приоритет голосовой трубке / наушникам, если есть
            await el.setSinkId("communications");
          } catch {
            await el.setSinkId("");
          }
        }
      } catch {
        /* ignore */
      }
    })();
  }, [speakerOn, remoteAudioRef]);

  // Сбрасываем громкую связь при завершении звонка
  useEffect(() => {
    if (ui.phase === "idle" || ui.phase === "missed" || ui.phase === "ended") {
      setSpeakerOn(false);
    }
  }, [ui.phase]);

  useRingtone(ui.phase === "incoming");

  const audio = (
    <audio
      ref={remoteAudioRef}
      className="voice-call-audio"
      autoPlay
      playsInline
      aria-hidden="true"
    />
  );

  if (ui.phase === "idle") {
    return <>{audio}</>;
  }

  const connected = ui.phase === "connected";
  const outgoing = ui.phase === "outgoing";
  const touchShield = connected || outgoing;

  return (
    <>
      {audio}
      <div
        className={`voice-call-overlay${touchShield ? " voice-call-overlay--touch-shield" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Звонок"
      >
        <div className="voice-call-overlay__panel">
          {ui.phase === "outgoing" ? (
            <>
              <div className="voice-call-overlay__caller">
                <div className="voice-call-overlay__caller-avatar">{ui.name[0]?.toUpperCase() ?? "?"}</div>
                <p className="voice-call-overlay__caller-name">@{ui.name}</p>
                <p className="voice-call-overlay__caller-label">Исходящий звонок…</p>
              </div>
              <LongPressHangup
                onHangup={onHangup}
                hint="Удерживайте кнопку, чтобы сбросить вызов"
                label="Сбросить"
                ariaLabel="Сбросить вызов — удерживайте кнопку"
              />
            </>
          ) : null}
          {ui.phase === "incoming" ? (
            <>
              <div className="voice-call-overlay__caller">
                <div className="voice-call-overlay__caller-avatar">{ui.name[0]?.toUpperCase() ?? "?"}</div>
                <p className="voice-call-overlay__caller-name">@{ui.name}</p>
                <p className="voice-call-overlay__caller-label">Входящий звонок</p>
              </div>
              <div className="voice-call-overlay__row">
                <button type="button" className="voice-call-overlay__btn voice-call-overlay__btn--accent" onClick={onAccept}>
                  Ответить
                </button>
                <button type="button" className="voice-call-overlay__btn voice-call-overlay__btn--reject" onClick={onReject}>
                  Отклонить
                </button>
              </div>
            </>
          ) : null}
          {ui.phase === "connected" ? (
            <>
              <div className="voice-call-overlay__caller">
                <div className="voice-call-overlay__caller-avatar">{ui.name[0]?.toUpperCase() ?? "?"}</div>
                <p className="voice-call-overlay__caller-name">@{ui.name}</p>
                <p className="voice-call-overlay__caller-label">Разговор</p>
              </div>
              <div className="voice-call-overlay__row voice-call-overlay__row--controls">
                <button
                  type="button"
                  className={`voice-call-overlay__btn voice-call-overlay__btn--icon${ui.muted ? " voice-call-overlay__btn--muted" : ""}`}
                  onClick={onToggleMute}
                  aria-label={ui.muted ? "Включить микрофон" : "Выключить микрофон"}
                  aria-pressed={ui.muted}
                >
                  {ui.muted ? "Микр. выкл." : "Микр. вкл."}
                </button>
                {canSetSinkId ? (
                  <button
                    type="button"
                    className={`voice-call-overlay__btn voice-call-overlay__btn--icon${speakerOn ? " voice-call-overlay__btn--speaker-on" : ""}`}
                    onClick={toggleSpeaker}
                    aria-label={speakerOn ? "Отключить громкую связь" : "Включить громкую связь"}
                    aria-pressed={speakerOn}
                  >
                    {speakerOn ? "Громко" : "Тихо"}
                  </button>
                ) : null}
              </div>
              <LongPressHangup
                onHangup={onHangup}
                hint="Удерживайте кнопку, чтобы завершить звонок"
                label="Завершить"
                ariaLabel="Завершить звонок — удерживайте кнопку"
              />
            </>
          ) : null}
          {ui.phase === "missed" || ui.phase === "ended" ? (
            <>
              <p className="voice-call-overlay__line">{ui.message}</p>
              <button type="button" className="voice-call-overlay__btn" onClick={onDismissInfo}>
                OK
              </button>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}
